import { Table } from 'react-bootstrap';
import { AuditLogFieldDetail } from '../models/auditLog.model';

function safeParseJson(s: string | null | undefined): unknown | null {
    if (s == null || s === '' || s === 'null') {
        return null;
    }
    const t = s.trim();
    if (!t.startsWith('{') && !t.startsWith('[')) {
        return null;
    }
    try {
        return JSON.parse(t);
    } catch {
        return null;
    }
}

function formatPrimitive(v: unknown): string {
    if (v === null || v === undefined) {
        return 'null';
    }
    if (typeof v === 'object') {
        return JSON.stringify(v, null, 2);
    }
    return String(v);
}

/** Comparación visual campo a campo (incluye intento de JSON anidado). */
export function KeyValueDiff({
    fieldName,
    oldValue,
    newValue,
}: {
    fieldName: string;
    oldValue: string;
    newValue: string;
}) {
    const oldP = safeParseJson(oldValue);
    const newP = safeParseJson(newValue);
    const bothObjects =
        oldP !== null &&
        newP !== null &&
        typeof oldP === 'object' &&
        typeof newP === 'object' &&
        !Array.isArray(oldP) &&
        !Array.isArray(newP);

    if (bothObjects) {
        return (
            <tr>
                <td colSpan={3}>
                    <div className='fw-medium mb-2'>{fieldName}</div>
                    <JsonObjectDiff
                        oldObj={oldP as Record<string, unknown>}
                        newObj={newP as Record<string, unknown>}
                    />
                </td>
            </tr>
        );
    }

    const changed = oldValue !== newValue;
    return (
        <tr className={changed ? 'table-warning' : ''}>
            <td className='text-break' style={{ minWidth: 140 }}>
                {fieldName}
            </td>
            <td className='text-break bg-danger bg-opacity-10'>
                <pre className='small mb-0 text-wrap' style={{ whiteSpace: 'pre-wrap' }}>
                    {oldValue === '' || oldValue === 'null' ? '—' : oldValue}
                </pre>
            </td>
            <td className='text-break bg-success bg-opacity-10'>
                <pre className='small mb-0 text-wrap' style={{ whiteSpace: 'pre-wrap' }}>
                    {newValue === '' || newValue === 'null' ? '—' : newValue}
                </pre>
            </td>
        </tr>
    );
}

function JsonObjectDiff({
    oldObj,
    newObj,
}: {
    oldObj: Record<string, unknown>;
    newObj: Record<string, unknown>;
}) {
    const keys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)])).sort(
        (a, b) => a.localeCompare(b)
    );

    return (
        <Table bordered size='sm' className='mb-0 small'>
            <thead>
                <tr>
                    <th>Campo</th>
                    <th>Antes</th>
                    <th>Después</th>
                </tr>
            </thead>
            <tbody>
                {keys.map((k) => {
                    const o = oldObj[k];
                    const n = newObj[k];
                    const os = formatPrimitive(o);
                    const ns = formatPrimitive(n);
                    const added = o === undefined && n !== undefined;
                    const removed = o !== undefined && n === undefined;
                    const modified = o !== undefined && n !== undefined && os !== ns;
                    return (
                        <tr
                            key={k}
                            className={
                                added ? 'table-success' : removed ? 'table-danger' : modified ? 'table-warning' : ''
                            }
                        >
                            <td className='text-break'>{k}</td>
                            <td className='text-break bg-danger bg-opacity-10'>
                                <pre className='small mb-0' style={{ whiteSpace: 'pre-wrap' }}>
                                    {o === undefined ? '—' : os}
                                </pre>
                            </td>
                            <td className='text-break bg-success bg-opacity-10'>
                                <pre className='small mb-0' style={{ whiteSpace: 'pre-wrap' }}>
                                    {n === undefined ? '—' : ns}
                                </pre>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
}

interface AuditDiffViewerProps {
    fields: AuditLogFieldDetail[];
}

/**
 * Comparación visual de valores antiguos vs nuevos por campo.
 * Soporta JSON anidado en strings cuando ambos lados son objetos JSON válidos.
 */
export function AuditDiffViewer({ fields }: AuditDiffViewerProps) {
    if (!fields?.length) {
        return (
            <p className='text-muted mb-0'>No hay campos para comparar en este registro.</p>
        );
    }

    return (
        <Table responsive bordered hover size='sm' className='mb-0'>
            <thead className='table-light'>
                <tr>
                    <th>Campo</th>
                    <th>Valor anterior</th>
                    <th>Valor nuevo</th>
                </tr>
            </thead>
            <tbody>
                {fields.map((f, idx) => (
                    <KeyValueDiff
                        key={`${f.fieldName}-${idx}`}
                        fieldName={f.fieldName}
                        oldValue={f.oldValue ?? ''}
                        newValue={f.newValue ?? ''}
                    />
                ))}
            </tbody>
        </Table>
    );
}
